import React from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";

interface SwitchProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  value?: string;
  className?: string;
  "aria-label"?: string;
}

/**
 * Switch component wrapping Radix UI Switch primitive.
 * Used for on/off toggles with sliding knob UI (Show All, device mute, etc.).
 *
 * Visual difference from Toggle: Switch has a sliding knob that moves between
 * positions, while Toggle is a button that changes appearance.
 *
 * Supports both controlled (checked) and uncontrolled (defaultChecked) modes.
 */
export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked,
      defaultChecked,
      onCheckedChange,
      disabled,
      required,
      name,
      value,
      className,
      ...props
    },
    forwardedRef
  ) => {
    return (
      <SwitchPrimitive.Switch
        ref={forwardedRef}
        checked={checked}
        defaultChecked={defaultChecked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        required={required}
        name={name}
        value={value}
        className={`inline-flex h-6 w-11 items-center rounded-full border-2 border-gray-500 bg-gray-950 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-gray-950 disabled:opacity-50 disabled:cursor-not-allowed ${
          className || ""
        }`}
        {...props}
      >
        <SwitchPrimitive.Thumb className="block h-4 w-4 rounded-full shadow-lg transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0.5 data-[state=checked]:bg-gray-200 data-[state=unchecked]:bg-gray-500" />
      </SwitchPrimitive.Switch>
    );
  }
);

Switch.displayName = "Switch";

export default Switch;
